'use client';

import {
  Checkbox,
  Skeleton,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TableBody as MuiTableBody,
  Table as MuiTable,
  TableContainer,
  Divider,
  Stack,
} from '@mui/material';
import { JobExecutionDetails } from '@repo/api-gw-sdk';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type {
  Cell,
  Column,
  ColumnDef,
  ExpandedState,
  OnChangeFn,
  PaginationState,
  Row,
  RowSelectionState,
  SortDirection,
  SortingState,
  TableOptions,
  Table as TableType,
  VisibilityState,
} from '@tanstack/react-table';
import classnames from 'classnames';
import React, { useMemo, useState, useEffect, type CSSProperties } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import { FiltersBar } from './filtersBar';
import tableStyles from './table.module.css';

import { PaginationBar } from '../shared/paginationBar';

interface RowData<T> {
  data: T[];
  showFiltersBar: boolean;
  isLoading: boolean;
  isMultiSelect: boolean;
  MultiSelectActionButton?: (props: { entities: T[] }) => React.ReactNode;
  disableSorting?: boolean;
  sorting?: SortingState;
  columns: ColumnDef<T>[];
  hiddenColumns?: string[];
  pagination?: {
    pageIndex: number;
    pageSize: number;
    recordsCount?: number;
    recordsLimit?: number;
    onChange: (pagination: PaginationState) => void;
    reachedLastPage?: boolean;
  };
  paginationBarActions?: React.ComponentType<{
    columns: Column<T>[];
  }>[];
  onSortingChanged?: (state: SortingState) => void;
  testId: string;
  entityLabel: string;
  stickyHeader?: boolean;
  initialSelectedId?: string;
  onRowSelectionChange?: (entities: string[]) => void;
  enableColumnResizing?: boolean;
}

const selectionColumn: ColumnDef<unknown> = {
  id: 'select',
  enableHiding: false,
  size: 53,
  cell: ({ row }) => {
    return (
      <Checkbox
        checked={row.getIsExpanded()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleExpandedHandler()}
      />
    );
  },
};

export const Table = <T extends object>({
  data,
  showFiltersBar,
  hiddenColumns,
  isMultiSelect,
  MultiSelectActionButton,
  columns,
  sorting,
  isLoading,
  pagination,
  disableSorting,
  onSortingChanged,
  testId,
  entityLabel,
  stickyHeader,
  paginationBarActions,
  initialSelectedId,
  onRowSelectionChange,
  enableColumnResizing,
}: RowData<T>) => {
  const [isTableScrolledVertically, setTableScrolledVertically] =
    useState(false);

  const extendedColumns = useMemo(
    () => [
      ...(isMultiSelect ? [selectionColumn as ColumnDef<T>] : []),
      ...columns,
    ],
    [isMultiSelect, columns]
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    initialSelectedId ? { [initialSelectedId]: true } : {}
  );

  const [expandedState, setExpandedState] = useState<ExpandedState>({});

  useEffect(() => {
    setRowSelection(initialSelectedId ? { [initialSelectedId]: true } : {});
  }, [initialSelectedId]);

  const { columnVisibility, onColumnVisibilityChange } =
    usePersistedColumnsVisibility(testId, extendedColumns, hiddenColumns);

  const tableOptions: TableOptions<T> = {
    data,
    columnResizeMode: enableColumnResizing ? 'onChange' : undefined,
    columns: extendedColumns,
    enableRowSelection: true,
    enableSortingRemoval: false,
    enableMultiRowSelection: isMultiSelect,
    getRowId: (row, index) => {
      if ('id' in row && typeof row.id === 'string') {
        return row.id;
      }

      if (
        'jobExecutionDetails' in row &&
        row.jobExecutionDetails instanceof JobExecutionDetails
      ) {
        return row.jobExecutionDetails.jobId;
      }

      return `${index}`;
    },
    state: {
      sorting,
      rowSelection,
      expanded: expandedState,
      columnVisibility,
    },
    onColumnVisibilityChange,
    getRowCanExpand: () => isMultiSelect,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: (state) => {
      const newState =
        typeof state === 'function' ? state(rowSelection) : state;
      setRowSelection(newState);
      onRowSelectionChange?.(Object.keys(newState));
      if (Object.keys(expandedState).length > 0) {
        table.resetExpanded(true);
      }
    },
    onExpandedChange: (state) => {
      const newState =
        typeof state === 'function' ? state(expandedState) : state;
      setExpandedState(newState);
      if (Object.keys(rowSelection).length > 0) {
        table.resetRowSelection(true);
      }
    },
    onSortingChange: (state) => {
      if (!disableSorting && sorting && onSortingChanged) {
        const newState = typeof state === 'function' ? state(sorting) : state;
        onSortingChanged(newState);
      }
    },
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable(tableOptions);

  const multiSelect = isMultiSelect
    ? {
        getIsAllRecordsSelected: table.getIsAllRowsExpanded,
        getIsSomeRecordsSelected: table.getIsSomeRowsExpanded,
        getToggleAllRecordsSelectedHandler:
          table.getToggleAllRowsExpandedHandler,
        ActionButton: MultiSelectActionButton ? (
          <MultiSelectActionButton entities={getCheckedEntities(table)} />
        ) : undefined,
      }
    : undefined;

  const onPageChange = (page: number) => {
    pagination?.onChange({
      pageSize: pagination.pageSize,
      pageIndex: page,
    });
  };

  const onRowsPerPageChange = (pageIndex: number, pageSize: number) => {
    pagination?.onChange({
      pageSize,
      pageIndex,
    });
  };

  return (
    <>
      {showFiltersBar && <FiltersBar columns={table.getAllColumns()} />}

      {(isMultiSelect || pagination || paginationBarActions?.length) && (
        <>
          <Divider />
          <PaginationBar
            pagination={pagination}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            entityLabel={entityLabel}
            selection={{
              multiSelect,
              selectedRecordsCount: getCheckedEntities(table).length,
            }}
            isLoading={isLoading}
            actions={paginationBarActions?.map((Component) => (
              <Component key={Component.name} columns={table.getAllColumns()} />
            ))}
          />
        </>
      )}

      <TableContainer
        className='flex-grow min-h-[500px]'
        onScroll={(event) => {
          const target = event.target as HTMLDivElement;
          if (typeof target.scrollTop === 'number') {
            const isScrolled = target.scrollTop > 0;
            if (isScrolled !== isTableScrolledVertically) {
              setTableScrolledVertically(isScrolled);
            }
          }
        }}
      >
        <MuiTable
          className={tableStyles.table}
          data-testid={testId}
          stickyHeader={stickyHeader}
        >
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={getColumnStyles(header.column)}
                    className={classnames({
                      'cursor-pointer select-none':
                        !disableSorting && header.column.getCanSort(),
                      [tableStyles.sorted]: header.column.getIsSorted(),
                      [tableStyles.scrollingVertically]:
                        isTableScrolledVertically,
                    })}
                    onClick={
                      !disableSorting && header.column.getCanSort()
                        ? () => {
                            header.column.toggleSorting();
                          }
                        : undefined
                    }
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                        >
                          <Typography variant='overline'>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </Typography>
                          {header.column.getCanSort() && (
                            <SortIcon isSorted={header.column.getIsSorted()} />
                          )}
                        </Stack>
                        {enableColumnResizing && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={tableStyles.resizer}
                          />
                        )}
                      </>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody<T>
            isLoading={isLoading}
            isMultiSelect={isMultiSelect}
            table={table}
            isRowSelected={(row) => row.getIsExpanded() || row.getIsSelected()}
          />
        </MuiTable>
      </TableContainer>
    </>
  );
};

const LoadingState = ({
  numOfRows,
  numOfColumns,
}: {
  numOfRows: number;
  numOfColumns: number;
}) => {
  return (
    <MuiTableBody>
      {[...Array(numOfRows)].map((_, i) => (
        <TableRow key={i}>
          {[...Array(numOfColumns)].map((_, j) => (
            <TableCell key={j} className='text-center'>
              <Skeleton variant='text' />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </MuiTableBody>
  );
};

const NoDataState = ({ columnCount }: { columnCount: number }) => {
  return (
    <MuiTableBody>
      <TableRow>
        <TableCell colSpan={columnCount} className='text-center'>
          No data available
        </TableCell>
      </TableRow>
    </MuiTableBody>
  );
};

function TableBody<T>({
  table,
  isLoading,
  isMultiSelect,
  isRowSelected,
}: {
  table: TableType<T>;
  isLoading: boolean;
  getDynamicColProps?: (cell: Cell<T, unknown>) => Record<string, unknown>;
  isMultiSelect: boolean;
  isRowSelected: (row: Row<T>) => boolean;
}) {
  if (isLoading) {
    return (
      <LoadingState
        numOfRows={10}
        numOfColumns={table.getVisibleFlatColumns().length}
      />
    );
  }

  if (table.getRowModel().rows.length === 0) {
    return <NoDataState columnCount={table.getVisibleFlatColumns().length} />;
  }

  return (
    <MuiTableBody>
      {table.getRowModel().rows.map((row) => {
        return (
          <TableRow
            key={row.id}
            data-selected={isRowSelected(row)}
            className={classnames({
              [tableStyles.selected]: isRowSelected(row),
            })}
          >
            {row.getVisibleCells().map((cell) => {
              return (
                <TableCell
                  key={cell.id}
                  sx={getColumnStyles(cell.column)}
                  className={classnames({
                    [tableStyles.multi]: isMultiSelect,
                  })}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </MuiTableBody>
  );
}

function getCheckedEntities<T>(table: TableType<T>) {
  const selectedEntities = table
    .getRowModel()
    .rows.filter((r) => r.getIsExpanded())
    .map((r) => r.original);
  return selectedEntities;
}

const getColumnStyles = <T,>(column: Column<T>): CSSProperties => {
  return {
    width: column.getSize(),
  };
};

const usePersistedColumnsVisibility = <T,>(
  tableKey: string,
  columns: ColumnDef<T>[] = [],
  defaultHiddenColumns: string[] = []
) => {
  const dal = useDAL();
  const { isDemo, isDev } = useEnvironment();
  const shouldPersistColumnVisibility = !isDemo && !isDev;

  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );

  const defaultColumnVisibility = useMemo(
    () =>
      columns.reduce<Record<string, boolean>>((acc, col) => {
        const id = ('accessorKey' in col && col.accessorKey) || col.id;
        if (typeof id === 'string') {
          acc[id] = !defaultHiddenColumns.includes(id);
        }
        return acc;
      }, {}),
    [columns, defaultHiddenColumns]
  );

  const [columnVisibility, setColumnVisibility] = useState(
    defaultColumnVisibility
  );

  const onColumnVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const newState =
      typeof updater === 'function' ? updater(columnVisibility) : updater;

    if (compareBooleanRecords(newState, columnVisibility)) {
      return;
    }

    setColumnVisibility(newState);

    if (!shouldPersistColumnVisibility) {
      return;
    }

    void dal.preferences
      .getUserPrefAsync(PreferencesKey.Preferences)
      .then((perf) => {
        const updatedValue = perf || {};
        updatedValue.value = updatedValue.value || {};
        updatedValue.value.tables = updatedValue.value.tables || {};
        updatedValue.value.tables[tableKey] =
          updatedValue.value.tables[tableKey] || {};

        const persistedValue =
          updatedValue.value.tables[tableKey].columnVisibility;
        if (
          (!persistedValue &&
            compareBooleanRecords(newState, defaultColumnVisibility)) ||
          compareBooleanRecords(newState, persistedValue || {})
        ) {
          return;
        }

        updatedValue.value.tables[tableKey].columnVisibility = newState;
        return dal.preferences.updateUserPref(
          PreferencesKey.Preferences,
          updatedValue.value,
          true
        );
      });
  };

  useEffect(() => {
    const persistedColumnVisibility =
      prefResponse?.value?.tables?.[tableKey]?.columnVisibility;
    if (!persistedColumnVisibility || !shouldPersistColumnVisibility) {
      return;
    }

    const mergedColumnVisibility = Object.entries(columnVisibility).reduce<
      Record<string, boolean>
    >((acc, [key, value]) => {
      acc[key] =
        key in persistedColumnVisibility
          ? persistedColumnVisibility[key]
          : value;
      return acc;
    }, {});

    onColumnVisibilityChange(mergedColumnVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefResponse]);

  return { columnVisibility, onColumnVisibilityChange };
};

function compareBooleanRecords(
  a: Record<string, boolean>,
  b: Record<string, boolean>
): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (const key of keysA) {
    if (a[key] !== b[key]) {
      return false;
    }
  }

  return true;
}

const SortIcon = ({ isSorted }: { isSorted: SortDirection | false }) =>
  isSorted && (
    <i
      className={classnames({
        'ri-arrow-down-s-line': isSorted === 'desc',
        'ri-arrow-up-s-line': isSorted === 'asc',
      })}
    />
  );
